// =============================================================================
// Component: ReportActions
// Actions for generating and downloading reports with queue support
// =============================================================================

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Download,
  Loader2,
  RefreshCw,
  Mail,
  FileText,
  Send,
  XCircle,
  History,
  Clock,
} from "lucide-react";
import { useReportQueue, useSubmissionReportStatus } from "@/hooks/useReportQueue";
import { ReportStatusBadge, getReportStatus } from "./ReportStatusBadge";
import { ReportHistoryDrawer } from "./ReportHistoryDrawer";
import { toast } from "sonner";

interface ReportActionsProps {
  submissionId: number;
  templateId: number;
  submissionName?: string;
  compact?: boolean;
}

export function ReportActions({
  submissionId,
  templateId,
  submissionName,
  compact = false,
}: ReportActionsProps) {
  const {
    enqueueReportAsync,
    downloadReport,
    retryJob,
    cancelJob,
    sendNotification,
    isEnqueuing,
    isSendingNotification,
  } = useReportQueue();

  const {
    isGenerating,
    hasReport,
    hasFailed,
    activeJob,
    latestFailedJob,
    report,
    refetch,
  } = useSubmissionReportStatus(submissionId, templateId);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isEnqueuingWithNotify, setIsEnqueuingWithNotify] = useState(false);
  const [isEnqueuingWithoutNotify, setIsEnqueuingWithoutNotify] = useState(false);

  const status = getReportStatus(hasReport, activeJob?.status, hasFailed);

  // Handle generate (queue without notification, for immediate download later)
  const handleGenerate = async () => {
    setIsEnqueuingWithoutNotify(true);
    try {
      await enqueueReportAsync({
        submissionId,
        templateId,
        priority: 1,
        notifyRecipient: false,
      });
      // Refetch once to update status immediately
      refetch();
    } catch (error) {
      // Error handled by hook
    } finally {
      setIsEnqueuingWithoutNotify(false);
    }
  };

  // Handle generate and notify (queue with email notification)
  const handleGenerateAndNotify = async () => {
    setIsEnqueuingWithNotify(true);
    try {
      await enqueueReportAsync({
        submissionId,
        templateId,
        priority: 1,
        notifyRecipient: true,
      });
      toast.success("Reporte encolado. Se enviará email al completar.");
      refetch();
    } catch (error) {
      // Error handled by hook
    } finally {
      setIsEnqueuingWithNotify(false);
    }
  };

  // Handle send report (just send email for existing report)
  const handleSendReport = () => {
    if (!report) {
      toast.error("No hay reporte generado para enviar");
      return;
    }
    // Send notification for existing report (doesn't regenerate)
    sendNotification(report.id);
  };

  // Handle download
  const handleDownload = async () => {
    if (!report) return;

    setIsDownloading(true);
    try {
      const fileName = submissionName
        ? `reporte_${submissionName.replace(/[^a-zA-Z0-9-_]/g, "_")}.pptx`
        : `reporte_${submissionId}.pptx`;

      await downloadReport(report.id, fileName);
    } finally {
      setIsDownloading(false);
    }
  };

  // Handle retry
  const handleRetry = () => {
    if (latestFailedJob) {
      retryJob(latestFailedJob.id);
      refetch();
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (activeJob) {
      cancelJob(activeJob.id);
    }
  };

  // Handle regenerate
  const handleRegenerate = async () => {
    await handleGenerate();
  };

  const isAnyLoading = isEnqueuing || isEnqueuingWithNotify || isEnqueuingWithoutNotify || isDownloading || isSendingNotification;

  // Compact mode - just icon buttons
  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <TooltipProvider>
          {hasReport && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Descargar reporte</TooltipContent>
            </Tooltip>
          )}

          {isGenerating && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" disabled>
                  <Loader2 className="h-4 w-4 animate-spin" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {activeJob?.status === "pending" ? "En cola..." : "Generando..."}
              </TooltipContent>
            </Tooltip>
          )}

          {!isGenerating && !hasReport && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleGenerate}
                  disabled={isAnyLoading}
                >
                  {isEnqueuingWithoutNotify ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Generar reporte</TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>
    );
  }

  // Full mode with all buttons visible
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Status Badge */}
      <ReportStatusBadge status={status} />

      {/* Processing state */}
      {isGenerating && (
        <TooltipProvider>
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={activeJob?.attempts && activeJob.attempts > 0 ? "outline" : "secondary"}
                  size="sm"
                  disabled
                  className={`cursor-help ${activeJob?.attempts && activeJob.attempts > 0 ? "border-amber-500 text-amber-600" : ""}`}
                >
                  {activeJob?.status === "pending" ? (
                    <>
                      <Clock className="h-4 w-4 mr-2 animate-pulse" />
                      {activeJob?.attempts && activeJob.attempts > 0
                        ? `Reintentando (${activeJob.attempts}/${activeJob.max_attempts})`
                        : "En cola"
                      }
                    </>
                  ) : (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generando...
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="font-medium">
                  {activeJob?.status === "pending"
                    ? activeJob?.attempts && activeJob.attempts > 0
                      ? "Reintentando automáticamente"
                      : "En cola de procesamiento"
                    : "Generando reporte"
                  }
                </p>
                <p className="text-xs text-muted-foreground">
                  {activeJob?.status === "pending"
                    ? "Se procesa automáticamente cada 5 minutos"
                    : "Esto puede tomar unos segundos"
                  }
                </p>
                {activeJob?.error_message && (
                  <p className="text-xs text-red-500 mt-1">
                    Error anterior: {activeJob.error_message}
                  </p>
                )}
              </TooltipContent>
            </Tooltip>
            {activeJob?.status === "pending" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCancel}
                    className="h-8 w-8"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Cancelar</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>
      )}

      {/* Has report - show download and send options */}
      {hasReport && !isGenerating && (
        <>
          <Button
            variant="default"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Descargar
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleSendReport}
            disabled={isAnyLoading}
          >
            {isSendingNotification ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Enviar por email
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleRegenerate}
            disabled={isAnyLoading}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Regenerar
          </Button>

          <ReportHistoryDrawer
            submissionId={submissionId}
            templateId={templateId}
            submissionName={submissionName}
            trigger={
              <Button variant="ghost" size="sm">
                <History className="h-4 w-4 mr-2" />
                Historial
              </Button>
            }
          />
        </>
      )}

      {/* No report and not processing - show generate options */}
      {!hasReport && !isGenerating && (
        <>
          <Button
            variant="default"
            size="sm"
            onClick={handleGenerate}
            disabled={isAnyLoading}
          >
            {isEnqueuingWithoutNotify ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Generar
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleGenerateAndNotify}
            disabled={isAnyLoading}
          >
            {isEnqueuingWithNotify ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-2" />
            )}
            Generar y enviar
          </Button>
        </>
      )}

      {/* Failed state - show retry */}
      {hasFailed && !isGenerating && latestFailedJob && (
        <Button
          variant="destructive"
          size="sm"
          onClick={handleRetry}
          disabled={isAnyLoading}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Reintentar
        </Button>
      )}

      {/* Refresh button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => refetch()}
        title="Actualizar estado"
        disabled={isAnyLoading}
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
    </div>
  );
}
